import { useLocale, useTranslation } from '@dreamstack/i18n'
import { SocialMediaFacebook } from '@dreamstack/icons/src/icon-components/SocialMediaFacebook'
import { SocialMediaInstagram } from '@dreamstack/icons/src/icon-components/SocialMediaInstagram'
import { SocialMediaLinkedin } from '@dreamstack/icons/src/icon-components/SocialMediaLinkedin'
import { SocialMediaXing } from '@dreamstack/icons/src/icon-components/SocialMediaXing'
import { SocialMediaYoutube } from '@dreamstack/icons/src/icon-components/SocialMediaYoutube'
import { trpc } from '@dreamstack/trpc/client'
import { formatPhoneForHref } from '@dreamstack/util'
import map from 'lodash/map'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import { FooterHeading, Text_16_24 } from '../layout/Texts'

const socials = [
  {
    Icon: () => (
      <SocialMediaFacebook className="transition-colors duration-150 hover:text-accentroAqua-full focus:text-accentroAqua-full" />
    ),
    className: 'social-facebook',
    href: 'https://www.facebook.com/Accentro-Immobilien-188970351240067/',
    ariaLabel: 'Go to Facebook',
  },
  {
    Icon: () => (
      <SocialMediaInstagram className="transition-colors duration-150 hover:text-accentroAqua-full focus:text-accentroAqua-full" />
    ),
    className: 'social-instagram',
    href: 'https://www.instagram.com/accentro.realestate/',
    ariaLabel: 'Go to Instagram',
  },
  {
    Icon: () => (
      <SocialMediaYoutube className="transition-colors duration-150 hover:text-accentroAqua-full focus:text-accentroAqua-full" />
    ),
    className: 'social-youtube',
    href: 'https://www.youtube.com/channel/UCQQCpohq8Q2m9mMJae2e-KA',
    ariaLabel: 'Go to YouTube',
  },
  {
    Icon: () => (
      <SocialMediaXing className="transition-colors duration-150 hover:text-accentroAqua-full focus:text-accentroAqua-full" />
    ),
    className: 'social-xing',
    href: 'https://www.xing.com/companies/accentrogmbh',
    ariaLabel: 'Go to Xing',
  },
  {
    Icon: () => (
      <SocialMediaLinkedin className="transition-colors duration-150 hover:text-accentroAqua-full focus:text-accentroAqua-full" />
    ),
    className: 'social-linkedin',
    href: 'https://www.linkedin.com/company/accentro',
    ariaLabel: 'Go to LinkedIn',
  },
  // TODO: Get icon and enable this
  // {
  //   Icon: PINTEREST,
  //   className: 'social-pinterest',
  //   href: 'https://www.pinterest.de/accentro/',
  // },
]

export const ContactFooter: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const t = useTranslation()
  const locale = useLocale()
  const { data: footer } = trpc.cms.getFooter.useQuery(
    { locale },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
    }
  )
  const formattedPhone = footer?.phone
    ? formatPhoneForHref({ digits: footer.phone })
    : undefined
  const formattedFax = footer?.fax
    ? formatPhoneForHref({ digits: footer.fax })
    : undefined

  return (
    <div>
      <FooterHeading className="mb-px-24">
        {t('accentro:footer.contactHeading')}
      </FooterHeading>
      <div className="flex flex-col mb-px-32 space-y-px-24 md:flex-row md:space-x-px-80 md:space-y-0">
        <div className="space-y-px-24 md:space-y-px-16">
          <div>
            <Text_16_24 className="font-semibold">
              {t('accentro:footer.telefon')}
            </Text_16_24>
            <Text_16_24>
              <a href={formattedPhone}>{footer?.phone}</a>
            </Text_16_24>
          </div>
          <div>
            <Text_16_24 className="font-semibold">
              {t('accentro:footer.fax')}
            </Text_16_24>
            <Text_16_24>
              <a href={formattedFax}>{footer?.fax}</a>
            </Text_16_24>
          </div>
          <div>
            <Text_16_24 className="font-semibold">E-Mail</Text_16_24>
            <Text_16_24 className="text-accentroAquaMidLight-full font-semibold">
              <a href="mailto:mail@accentro.de">mail@accentro.de</a>
            </Text_16_24>
          </div>
        </div>
        <div>
          <Text_16_24 className="font-semibold">
            {t('accentro:footer.address')}
          </Text_16_24>
          <Text_16_24>ACCENTRO Berlin</Text_16_24>
          <Text_16_24>Kantstraße 44/45</Text_16_24>
          <Text_16_24>10625 Berlin</Text_16_24>
        </div>
      </div>
      <div className="flex space-x-px-12">
        {map(socials, ({ href, className, Icon, ariaLabel }) => (
          <Link
            href={href}
            passHref
            key={className}
            target="_blank"
            className={className}
            aria-label={ariaLabel}
          >
            <Icon />
          </Link>
        ))}
      </div>
    </div>
  )
}
